import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { OnboardingSteps } from "../components/OnboardingSteps";
import { onboarding as onboardingApi } from "../api/endpoints";
import { useAuth } from "../store/auth";
import { useConfig } from "../store/config";

/**
 * The last onboarding step creates the contributor's on-air SpeedRadio
 * account, so it runs on arrival rather than behind another button — there is
 * nothing left for the person to decide.
 */
export function OnboardingFinish() {
  const setContributor = useAuth((state) => state.setContributor);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const result = await onboardingApi.complete();
        setContributor(result.contributor);
        setDone(true);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [setContributor]);

  // From the API, so this cannot claim a limit the API does not enforce.
  const recording = useConfig((state) => state.config.recording);

  return (
    <Layout showNav={false} title="All Set">
      <section className="section">
        <div className="wrap narrow">
          <OnboardingSteps current="finish" />

          {!done && !error && (
            <div className="center-page">
              <div style={{ display: "grid", placeItems: "center", gap: 14 }}>
                <div className="spinner" />
                <div className="muted">Setting up your account…</div>
              </div>
            </div>
          )}

          {error && (
            <div className="panel">
              <div className="card-label">Something went wrong</div>
              <p style={{ marginTop: 0 }}>{error}</p>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  startedRef.current = false;
                  setError(null);
                  navigate(0);
                }}
              >
                Try again
              </button>
            </div>
          )}

          {done && (
            <>
              <div className="eyebrow">You're in</div>
              <h1 className="display" style={{ fontSize: "clamp(26px,5vw,40px)" }}>
                Ready to <span className="accent">record.</span>
              </h1>
              <ol className="steps-list">
                <li>
                  <span className="txt">
                    <b>Record in your own voice.</b> Up to {recording.max_seconds} seconds —
                    around {recording.ideal_min_seconds}–{recording.ideal_max_seconds} works
                    best. Natural delivery.
                  </span>
                </li>
                <li>
                  <span className="txt">
                    <b>Get paid.</b> Our team reviews each note; accepted ones add to your
                    balance.
                  </span>
                </li>
              </ol>

              <button
                type="button"
                className="btn btn-primary mt-24"
                onClick={() => navigate("/record", { replace: true })}
              >
                Start recording →
              </button>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
